import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, CheckCircle, Package, Wrench, Phone, Camera,
  Calendar, User, ClipboardCheck, AlertTriangle,
} from 'lucide-react';
import { api } from '@/utils/api';

interface PreVisitChecklistProps {
  productType: 'ss_water_tank' | 'heat_pump' | 'swh';
  onComplete: () => void;
}

const TOOLS_SSWATER = [
  'Leakage detection kit', 'Pressure gauge', 'Wrench set', 'Sealant tape',
  'Replacement gaskets', 'Water level indicator', 'Thermometer', 'Torch',
];

const TOOLS_HEATPUMP = [
  'Multimeter', 'Pressure gauge', 'Refrigerant gauge', 'Wrench set',
  'Screwdriver set', 'Vacuum pump', 'Gas leak detector', 'Thermal camera',
];

const TOOLS_SWH = [
  'Leakage detection kit', 'Vacuum gauge', 'Wrench set', 'Glass cutter',
  'Vacuum tube holder', 'Sealant tape', 'Replacement gaskets', 'Thermometer',
];

const SAFETY_KIT = [
  'Safety gloves', 'Safety goggles', 'Safety shoes', 'First aid kit',
  'Fire extinguisher', 'Insulation gloves', 'Helmet', 'Safety vest',
];

export function PreVisitChecklistPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [checklist, setChecklist] = useState({
    toolsChecked: [] as string[],
    safetyChecked: [] as string[],
    customerConfirmed: false,
    preDiagnosisPhotos: [] as string[],
    notes: '',
  });

  const getTools = (productType: string) => {
    switch (productType) {
      case 'heat_pump': return TOOLS_HEATPUMP;
      case 'swh': return TOOLS_SWH;
      default: return TOOLS_SSWATER;
    }
  };

  const toggleTool = (tool: string) => {
    setChecklist((c) => ({
      ...c,
      toolsChecked: c.toolsChecked.includes(tool)
        ? c.toolsChecked.filter((t) => t !== tool)
        : [...c.toolsChecked, tool],
    }));
  };

  const toggleSafety = (item: string) => {
    setChecklist((c) => ({
      ...c,
      safetyChecked: c.safetyChecked.includes(item)
        ? c.safetyChecked.filter((i) => i !== item)
        : [...c.safetyChecked, item],
    }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/files', formData);
      const url = res.data.url || res.data.data?.url;
      if (url) setChecklist((c) => ({ ...c, preDiagnosisPhotos: [...c.preDiagnosisPhotos, url] }));
    } catch {
      const reader = new FileReader();
      reader.onload = () => {
        setChecklist((c) => ({ ...c, preDiagnosisPhotos: [...c.preDiagnosisPhotos, reader.result as string] }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!id) return;
    if (checklist.toolsChecked.length === 0 || checklist.safetyChecked.length === 0) {
      alert('Please check all tools and safety items');
      return;
    }
    if (!checklist.customerConfirmed) {
      alert('Please confirm customer availability');
      return;
    }

    setSubmitting(true);
    try {
      await api.patch(`/service-sop/complaints/${id}/pre-visit`, {
        toolsChecked: checklist.toolsChecked,
        safetyChecked: checklist.safetyChecked,
        customerConfirmed: checklist.customerConfirmed,
        preDiagnosisPhotos: checklist.preDiagnosisPhotos,
        notes: checklist.notes,
      });
      navigate(`/service-tickets/${id}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const allToolsChecked = (tools: string[]) => 
    tools.every((t) => checklist.toolsChecked.includes(t));
  const allSafetyChecked = SAFETY_KIT.every((s) => checklist.safetyChecked.includes(s));

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Pre-Visit Checklist</h1>
        <p className="text-sm text-[var(--text-muted)]">Prepare for on-site service visit</p>
      </div>

      <div className="space-y-6">
        {/* Tools Checklist */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Wrench size={18} className="text-[var(--ink-0)]" />
              Tools & Equipment
            </h3>
            <span className={`text-xs px-2 py-1 rounded-full ${allToolsChecked(TOOLS_SSWATER) ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {checklist.toolsChecked.length} / {TOOLS_SSWATER.length} checked
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {TOOLS_SSWATER.map((tool) => (
              <label
                key={tool}
                className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${
                  checklist.toolsChecked.includes(tool)
                    ? 'border-green-300 bg-green-50'
                    : 'border-[var(--border-primary)] hover:bg-[var(--bg-muted)]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checklist.toolsChecked.includes(tool)}
                  onChange={() => toggleTool(tool)}
                  className="hidden"
                />
                {checklist.toolsChecked.includes(tool) ? (
                  <CheckCircle size={16} className="text-green-500" />
                ) : (
                  <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                )}
                <span className="text-sm text-[var(--text-secondary)]">{tool}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Safety Kit */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Package size={18} className="text-[var(--ink-0)]" />
              Safety Kit
            </h3>
            <span className={`text-xs px-2 py-1 rounded-full ${allSafetyChecked ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {checklist.safetyChecked.length} / {SAFETY_KIT.length} checked
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {SAFETY_KIT.map((item) => (
              <label
                key={item}
                className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${
                  checklist.safetyChecked.includes(item)
                    ? 'border-green-300 bg-green-50'
                    : 'border-[var(--border-primary)] hover:bg-[var(--bg-muted)]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checklist.safetyChecked.includes(item)}
                  onChange={() => toggleSafety(item)}
                  className="hidden"
                />
                {checklist.safetyChecked.includes(item) ? (
                  <CheckCircle size={16} className="text-green-500" />
                ) : (
                  <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                )}
                <span className="text-sm text-[var(--text-secondary)]">{item}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Customer Confirmation */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-4">
            <User size={18} className="text-[var(--ink-0)]" />
            Customer Availability
          </h3>
          <label className={`flex items-center gap-3 cursor-pointer p-4 rounded-lg border transition-colors ${
            checklist.customerConfirmed
              ? 'border-green-300 bg-green-50'
              : 'border-[var(--border-primary)] hover:bg-[var(--bg-muted)]'
          }`}>
            <input
              type="checkbox"
              checked={checklist.customerConfirmed}
              onChange={(e) => setChecklist((c) => ({ ...c, customerConfirmed: e.target.checked }))}
              className="hidden"
            />
            {checklist.customerConfirmed ? (
              <CheckCircle size={20} className="text-green-500" />
            ) : (
              <Phone size={20} className="text-[var(--text-muted)]" />
            )}
            <div>
              <span className="font-medium text-[var(--text-primary)]">Customer confirmed available</span>
              <p className="text-xs text-[var(--text-muted)]">Pre-visit call completed</p>
            </div>
          </label>
        </div>

        {/* Pre-Diagnosis Photos */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-4">
            <Camera size={18} className="text-[var(--ink-0)]" />
            Pre-Diagnosis Photos
          </h3>
          <div className="flex flex-wrap gap-2">
            {checklist.preDiagnosisPhotos.map((url, idx) => (
              <div key={idx} className="relative">
                <img src={url} alt="" className="h-20 w-20 rounded-lg object-cover border" />
                <button
                  type="button"
                  onClick={() => setChecklist((c) => ({ ...c, preDiagnosisPhotos: c.preDiagnosisPhotos.filter((_, i) => i !== idx) }))}
                  className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            ))}
            <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-[var(--border-primary)] hover:bg-[var(--bg-muted)]">
              <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              <Camera size={20} className="text-[var(--text-muted)]" />
            </label>
          </div>
        </div>

        {/* Notes */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-2">Additional Notes</h3>
          <textarea
            value={checklist.notes}
            onChange={(e) => setChecklist((c) => ({ ...c, notes: e.target.value }))}
            rows={3}
            placeholder="Any special instructions or observations..."
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none"
          />
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(`/service-tickets/${id}`)}
            className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !allToolsChecked(TOOLS_SSWATER) || !allSafetyChecked || !checklist.customerConfirmed}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            Start On-Site Visit
          </button>
        </div>
      </div>
    </div>
  );
}